// @flow
import React, { useMemo, useCallback } from "react";
import { useAsyncMemo } from "hooks";
import ActionPanel from "components/ActionPanel";
import { createModal } from "react-modal-promise";
import { isListed } from "data/utils";
import { context } from "data/api";
import {
    type ItemActionConfigBase,
    type ItemActionConfig,
    type ItemActionCategoryConfig,
    type ItemActionsConfigDTO,
    ItemContextsFlags,
} from "data/types";
import CommandActionHandler from "components/CommandActions";
import { shouldBeVisible } from "./ActionPanel_shouldBeVisible";
import store from "data/store";
import * as s from "data/reducers/selectors";
import { getItemActionsConfig } from "data/itemActions";
import { getDefaultsConfig } from "data/constants";
import unionBy from "lodash/unionBy";
import cloneDeep from "lodash/cloneDeep";
import { asyncFilter } from "data/utils";

export /**
 * returns true if item (itemAction or category) should be shown to the user
 *
 * @param {ItemActionConfigBase} item action or category to render
 * @param {string} itemUri current item
 * @param {number} formatId current formatId
 * @param {Object} contextProps current contextProps
 * @param {string} [targetView] override view context (from store)
 * @returns {boolean} true if should render, false to hide
 */
const shouldRenderItem = async (
    item: ItemActionConfigBase,
    itemUri: string,
    formatId: number,
    contextProps: Object,
    targetView: ?string
): Promise<boolean> => {
    if (contextProps == null) return false;
    if (item.visible === false) return false;
    if (
        item.views &&
        !check_isView({
            views: item.views,
            targetView,
        })
    )
        return false;
    if (
        item.itemUriRegex &&
        !check_itemUriRegex({
            itemUri,
            itemUriRegex: item.itemUriRegex,
        })
    )
        return false;
    if (
        item.formatIds &&
        !check_formatId({
            formatId,
            formatIds: item.formatIds,
        })
    )
        return false;

    // Add backwards compatibility
    const backwardsCompatibleShouldBeVisible = (
        item.shouldBeVisible ?? [
            "!isRecycleBin",
            "!isSpecialFolder",
            "!isMultiSelection",
            "isOnline",
        ]
    ).slice();

    // hide everything from recyclebin
    if (
        !backwardsCompatibleShouldBeVisible.some((rule) =>
            rule.endsWith("isRecycleBin")
        )
    ) {
        backwardsCompatibleShouldBeVisible.push("!isRecycleBin");
    }
    // hide everything from special folder
    if (
        !contextProps.multiple &&
        !backwardsCompatibleShouldBeVisible.some((rule) =>
            rule.endsWith("isSpecialFolder")
        )
    ) {
        backwardsCompatibleShouldBeVisible.push("!isSpecialFolder");
    }
    // hide everything on multi selection
    if (
        !backwardsCompatibleShouldBeVisible.some(
            (rule) =>
                rule.endsWith("isMultiSelection") ||
                rule.endsWith("isAnySelection")
        )
    ) {
        backwardsCompatibleShouldBeVisible.push("!isMultiSelection");
    }
    // hide everything offline...
    if (
        !backwardsCompatibleShouldBeVisible.some(
            (rule) =>
                rule.endsWith("isOnline") || rule.endsWith("isAnyOnlineStatus")
        )
    ) {
        backwardsCompatibleShouldBeVisible.push("isOnline");
    }

    // apply shouldBeVisible
    return (
        (await shouldBeVisible({
            item: {
                ...item,
                shouldBeVisible: backwardsCompatibleShouldBeVisible,
            },
            itemUri,
            contextProps,
        })) === true
    );
};

const getContextProps = async (
    contextProps: any,
    itemContexts = ItemContextsFlags.ResultFlags | ItemContextsFlags.Offline
) => {
    const { itemUri, formatId, ext, name, itemStatus, multiple, items } =
        contextProps;
    /* istanbul ignore else */
    if (itemStatus == null) {
        itemContexts |= ItemContextsFlags.ItemStatus;
    }
    /* istanbul ignore else */
    if (name == null) {
        itemContexts |= ItemContextsFlags.WebDavName;
    }
    /* istanbul ignore else */
    if (ext == null) {
        itemContexts |= ItemContextsFlags.Extension;
    }
    const itemUris = multiple ? items.map((i) => i.itemUri) : [itemUri];
    let formatIds = multiple ? items.map((i) => i.formatId) : [formatId];
    if (formatIds.filter((i) => i != null).length === 0) {
        formatIds = null;
    }
    return await context(itemUris, formatIds, itemContexts);
};

type Props = {
    /** react-modal-promise: whether to show dialog */
    open: boolean,
    /** react-modal-promise: callback when user closes Dialog */
    close: () => void,
    /** React Component to render as Title */
    title?: React.Node,
    /** React Component to render as body */
    body?: React.Node,
    /** any context props passed by caller will be assigned to commandAction props */
    contextProps: {
        itemUri: string,
        formatId: number,
        multiple: boolean,
        items: Array<any>,
    } & Object,
    /** Override view context for Action Panel items */
    targetView?: string,
};

export const ActionPanelContainer = (props: Props) => {
    const { contextProps, targetView, ...rest } = props;

    // fetch context and extend contextProps
    const allContextProps = useAsyncMemo(async () => {
        try {
            return {
                ...contextProps,
                ...(await getContextProps(contextProps)),
            };
        } catch (e) {
            console.error(`Cannot calculate contextProps: ${e.message}`);
            return contextProps;
        }
    }, [contextProps]);

    const [itemUri, formatId] = useMemo(() => {
        if (allContextProps == null) {
            return [null, -1];
        }
        const { itemUri, folderItemUri, formatId, multiple, items } =
            allContextProps;
        if (!multiple) {
            return [itemUri, formatId];
        }
        const combinedFormatId = items.every(
            (item) => item.formatId === items[0].formatId
        )
            ? items[0].formatId
            : -1;
        return [folderItemUri, combinedFormatId];
    }, [allContextProps]);

    const itemActions = useMemo(
        () => mergeItemActionsConfig(itemUri, formatId),
        [itemUri, formatId]
    );

    const categories: Array<ItemActionCategoryConfig> = useAsyncMemo(
        async () =>
            await asyncFilter(
                itemActions.categories,
                (category: ItemActionCategoryConfig) =>
                    shouldRenderItem(
                        category,
                        itemUri,
                        formatId,
                        allContextProps,
                        targetView
                    )
            ),
        [itemActions, itemUri, formatId, allContextProps, targetView]
    );

    const actions: Array<ItemActionConfig> = useAsyncMemo(async () => {
        const result = await asyncFilter(
            itemActions.actions,
            (action: ItemActionConfig) =>
                shouldRenderItem(
                    action,
                    itemUri,
                    formatId,
                    allContextProps,
                    targetView
                )
        );

        // HACK itemViews_* use the CommandActionHandler to RENDER before onAction is being called!
        result.forEach(
            (action) =>
                action.commandAction &&
                (action.commandAction.props = Object.assign(
                    {},
                    action.commandAction.props,
                    allContextProps
                ))
        );
        return result;
    }, [itemActions, itemUri, formatId, allContextProps, targetView]);

    const onAction = useCallback(
        (action: ItemActionConfig) => {
            if (action.commandAction == null) {
                alert(
                    `Sorry, no CommandAction defined for action.name='${action.name}'`
                );
            } else {
                CommandActionHandler(
                    action.commandAction.name,
                    Object.assign(
                        {},
                        action.commandAction.props,
                        allContextProps
                    )
                );
            }
        },
        [allContextProps]
    );

    return (
        <ActionPanel
            isLoading={allContextProps == null}
            {...rest}
            actions={actions}
            categories={categories}
            onAction={onAction}
        />
    );
};
/* istanbul ignore next */
const MemoActionPanelContainer = React.memo(
    ActionPanelContainer,
    (prevProps, nextProps) => prevProps.itemUri !== nextProps.itemUri
);
export const openActionPanel = createModal(MemoActionPanelContainer);

export /**
 * checks if view is (not) included in list (case-insensitive)
 * negate with "!" as prefix
 *
 * @param {{Array<string>}} views to search
 * @param {{?string}} targetView to (not) search for in views
 * @returns {boolean} true if match found
 */
const check_isView = ({
    views,
    targetView,
}: {
    views: Array<string>,
    targetView: ?string,
}) => {
    if (views.length === 0) return false;
    const targetViewOrCurrent = targetView
        ? targetView
        : s.currentNameSelector(store.getState());
    const negatedViews = views
        .filter((l) => l.startsWith("!"))
        .map((l) => l.substr(1));
    const defaultViews = views.filter((l) => !l.startsWith("!"));
    return (
        (negatedViews.length === 0 ||
            !isListed(targetViewOrCurrent, negatedViews)) &&
        (defaultViews.length === 0 ||
            isListed(targetViewOrCurrent, defaultViews))
    );
};

export /**
 * checks if itemUri matches the itemUriRegex regular expression
 * @returns {boolean} true if match found
 */
const check_itemUriRegex = ({
    itemUri,
    itemUriRegex,
}: {
    itemUri: string,
    itemUriRegex: string,
}) => new RegExp(itemUriRegex, "i").exec(itemUri) !== null;

export /**
 * checks if formatId is included in the formatIds list
 * @returns {boolean} true if included
 */
const check_formatId = ({
    formatId,
    formatIds,
}: {
    formatId: number,
    formatIds: Array<number>,
}) => formatIds.includes(formatId);

/** merges our data/itemActions.js with customer defined actions from appsettings.json */
export const mergeItemActionsConfig = (
    itemUri: string,
    formatId: number
): ItemActionsConfigDTO => {
    // get itemActions as defined in appsettings.json (after being checked for security for this user)
    const userActions = cloneDeep(s.userItemActionsSelector(store.getState()));
    const itemActions = cloneDeep(getItemActionsConfig());

    // get itemUri specific itemActions
    const defaults = getDefaultsConfig(itemUri, formatId);
    if (defaults && defaults.itemActions) {
        if (defaults.itemActions.replace === true) {
            return {
                actions:
                    mergePartialConfig(
                        defaults.itemActions.actions,
                        itemActions.actions
                    ) || [],
                categories:
                    defaults.itemActions.categories || itemActions.categories, // use our system categories in case they are not specified
            };
        } else {
            return {
                actions: unionBy(
                    defaults.itemActions.actions,
                    userActions.actions,
                    itemActions.actions,
                    "id"
                ),
                categories: unionBy(
                    defaults.itemActions.categories,
                    userActions.categories,
                    itemActions.categories,
                    "id"
                ),
            };
        }
    } else {
        return {
            actions: unionBy(userActions.actions, itemActions.actions, "id"),
            categories: unionBy(
                userActions.categories,
                itemActions.categories,
                "id"
            ),
        };
    }
};

const mergePartialConfig = (userActions, sysActions) => {
    let result = cloneDeep(userActions);
    if (result)
        result.forEach((userAction) => {
            // is the userAction trying to override an existing system action (by id)?
            const sysIndex = sysActions.findIndex(
                (sa) => sa.id === userAction.id
            );
            if (sysIndex !== -1) {
                const sysAction = sysActions[sysIndex];
                // merge properties (in case user missed/does not want to specify any)
                Object.keys(sysAction).forEach((key) => {
                    userAction[key] = userAction[key] || sysAction[key];
                });
            }
        });
    return result;
};
